import { USER_PROFILE_STORAGE_KEY } from '@/constants'
import client, { uploadFile, PaginatedResponse } from '@/utils/client'
import storage from '@/utils/storage'
import urls from '@/utils/urls'

import * as mutationTypes from '../mutationTypes'
import { profileHelpers } from '../helpers'

const state = {
  user: storage.load(USER_PROFILE_STORAGE_KEY) || {},
  profileCache: {},
  fanInbox: null,
  artistInbox: null,
  followedArtists: null
}

const getters = {
  currentUser: state => state.user,
  displayName: state => profileHelpers.displayName(state.user),
  avatarImage: state => profileHelpers.avatarImage(state.user),
  heroImage: state => profileHelpers.heroImage(state.user),
  fanInbox: state => state.fanInbox,
  artistInbox: state => state.artistInbox,
  isArtist: state => !!(state.user && state.user.artist_id),
  dobDate: state => (state.user && state.user.dob) ? new Date(state.user.dob + 'T00:00:00Z') : null,
  followedArtists: state => state.followedArtists || []
}

const mutations = {
  [mutationTypes.SET_CURRENT_USER] (state, payload) {
    state.user = payload

    // Store user profile into local storage
    storage.save(USER_PROFILE_STORAGE_KEY, payload)
  },

  [mutationTypes.CLEAR_CURRENT_USER] (state) {
    state.user = {}
    state.fanInbox = null
    state.artistInbox = null

    // Remove user profile from local storage
    storage.remove(USER_PROFILE_STORAGE_KEY)
  },

  [mutationTypes.SET_FAN_INBOX] (state, payload) {
    state.fanInbox = payload
  },

  [mutationTypes.SET_ARTIST_INBOX] (state, payload) {
    state.artistInbox = payload
  },

  [mutationTypes.SET_USER_DOB] (state, payload) {
    if (payload) {
      state.user.dob = payload.toJSON().split(/T/)[0]
    } else {
      state.user.dob = null
    }
  },

  [mutationTypes.CACHE_PROFILE] (state, payload) {
    state.profileCache[payload.id] = payload
  },

  setFollowedArtists (state, artists) {
    state.followedArtists = artists
  }
}

const actions = {
  async getCurrentUser ({ commit, dispatch, state }) {
    try {
      const resp = await client.get(urls.profile.me())

      commit(mutationTypes.SET_CURRENT_USER, resp.data)

      if (state.fanInbox === null) {
        dispatch('getInboxes')
      }

      return resp.data
    } catch (e) {
      // TODO: Handle api error
    }
  },

  async updateCurrentUser ({ commit, state }) {
    try {
      const resp = await client.put(urls.profile.me(), state.user)

      commit(mutationTypes.SET_CURRENT_USER, resp.data)
    } catch (e) {
      // TODO: Handle api error
    }
  },

  async setUserAvatar ({ dispatch }, payload) {
    const { file } = payload

    try {
      await uploadFile(urls.profile.avatar(), file)
      dispatch('getCurrentUser')
    } catch (e) {
      dispatch('flash/showMessage', {message: 'Error uploading avatar image', type: 'error'}, {root: true})
    }
  },

  async deleteUserAvatar ({ dispatch }) {
    try {
      await client.delete(urls.profile.avatar())

      dispatch('getCurrentUser')
    } catch (e) {
      // TODO: Handle api error
    }
  },

  async setUserHeroImage ({ dispatch }, payload) {
    const { file } = payload

    try {
      await uploadFile(urls.profile.hero(), file)
      dispatch('getCurrentUser')
    } catch (e) {
      dispatch('flash/showMessage', {message: 'Error uploading profile image', type: 'error'}, {root: true})
    }
  },

  async deleteUserHeroImage ({ dispatch }) {
    try {
      await client.delete(urls.profile.hero())

      dispatch('getCurrentUser')
    } catch (e) {
      // TODO: Handle api error
    }
  },

  async deactivate ({ dispatch }) {
    await client.post(urls.profile.deactivate())
    dispatch('auth/logout', true, { root: true })
  },

  async findProfile (ctx, { handle }) {
    let response = await client.get(urls.profile.list(), { params: { handle } })
    return response.data
  },

  getProfile ({ commit, state }, payload) {
    const { id } = payload

    if (id === state.user.id) return Promise.resolve(state.user)

    if (id in state.profileCache) return Promise.resolve(state.profileCache[id])

    return client
      .get(urls.profile.detail(id))
      .then(response => {
        commit(mutationTypes.CACHE_PROFILE, response.data)
        return response.data
      })
  },

  async followProfile ({ dispatch }, { id }) {
    let response = await client.post(urls.profile.follow(id))
    await dispatch('refreshFollowedArtists')
    return response
  },

  async unfollowProfile ({ dispatch }, { id }) {
    let response = await client.post(urls.profile.unfollow(id))
    await dispatch('refreshFollowedArtists')
    return response
  },

  async reportProfile ({ dispatch }, { id, reason, url }) {
    return client.post(urls.profile.report(id), { reason, url })
  },

  async blockProfile ({ dispatch }, { id }) {
    return client.post(urls.profile.block(id))
  },

  async unblockProfile ({ dispatch }, { id }) {
    return client.post(urls.profile.unblock(id))
  },

  async listBlockedProfiles (context) {
    let response = await client.get(urls.profile.list(), { params: { blocked: '1' } })
    return new PaginatedResponse(response.data)
  },

  async refreshFollowedArtists ({ commit }) {
    let followed = []
    let response = new PaginatedResponse((await client.get(urls.profile.following())).data)

    while (response.count > 0) {
      for (let user of response.results) {
        followed.push(user)
      }
      response = response.next()
    }

    commit('setFollowedArtists', followed)
    return followed
  },

  async listFollowedArtists ({ state, dispatch }) {
    if (state.followedArtists !== null) {
      return state.followedArtists
    }
    return dispatch('refreshFollowedArtists')
  },

  async listFollowers ({ dispatch }) {
    const response = await client.get(urls.profile.followers())
    dispatch('artist/setFollowerCount', response.data.count, {root: true})
    return new PaginatedResponse(response.data)
  },

  getInboxes ({ commit, state }) {
    if (state.fanInbox) {
      return Promise.resolve({
        fanInbox: state.fanInbox,
        artistInbox: state.artistInbox
      })
    }

    return client
      .get(urls.profile.inboxes())
      .then(response => {
        const inboxes = response.data.results
        let fanInbox = null
        let artistInbox = null

        for (let inbox of inboxes) {
          if (inbox.is_artist_inbox) {
            artistInbox = inbox
          } else {
            fanInbox = inbox
          }
        }

        commit(mutationTypes.SET_FAN_INBOX, fanInbox)
        commit(mutationTypes.SET_ARTIST_INBOX, artistInbox)

        return {
          fanInbox,
          artistInbox
        }
      })
  },

  listFanConversations ({ dispatch }) {
    return dispatch('getInboxes')
      .then(inboxes => client.get(urls.profile.conversations(inboxes.fanInbox.id)))
      .then(response => new PaginatedResponse(response.data))
  },

  listArtistConversations ({ dispatch, state }) {
    if (!state.user.artist_id) {
      throw new Error('User is not an artist')
    }

    return dispatch('getInboxes')
      .then(inboxes => client.get(urls.profile.conversations(inboxes.artistInbox.id)))
      .then(response => new PaginatedResponse(response.data))
  },

  async listConversations ({ dispatch }, { inboxId }) {
    const response = await client.get(urls.profile.conversations(inboxId))
    return new PaginatedResponse(response.data)
  },

  async sendMessage ({ dispatch }, payload) {
    let { peerId, text, inboxId = null, sendAsArtist = false } = payload

    if (inboxId === null) {
      let inboxes = await dispatch('getInboxes')
      inboxId = (sendAsArtist && inboxes.artistInbox) ? inboxes.artistInbox.id : inboxes.fanInbox.id
    }

    let response = await client.post(urls.profile.messages(inboxId, peerId), { text })
    return response.data
  },

  listMessages (context, payload) {
    const { inboxId, peerId } = payload

    return client
      .get(urls.profile.messages(inboxId, peerId))
      .then(response => new PaginatedResponse(response.data))
  },

  async markAsRead (context, payload) {
    const { inboxId, peerId } = payload

    let response = await client.post(urls.profile.markAsRead(inboxId, peerId))
    return response.data.unread_count
  },

  donate (context, payload) {
    let { id, amount, liveId = undefined } = payload
    amount = Number(amount).toFixed(2)

    return client
      .post(urls.artist.donate(id), { amount, live_id: liveId })
      .then(response => response.data)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/profile.js